import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import { apiService } from "../../../services/api";
import { AttachmentModal } from "../deals/components/AttachmentModal";

interface DocumentoOportunidade {
    id: number;
    titulo: string;
    data_upload: string;
    tipo: string;
    descricao: string;
    nome_arquivo: string;
    usuario_nome?: string;
    tamanho?: number;
}

interface DocumentosOportunidadeProps {
    oportunidadeId: number;
}

const DocumentosOportunidade: React.FC<DocumentosOportunidadeProps> = ({
    oportunidadeId
}) => {
    const [documentos, setDocumentos] = useState<DocumentoOportunidade[]>([]);
    const [loading, setLoading] = useState(true);
    const [showAttachmentModal, setShowAttachmentModal] = useState(false);

    // Carregar documentos da oportunidade
    const loadDocumentos = async () => {
        try {
            setLoading(true);

            const anexosRes = await apiService.getAnexos({
                id_oportunidade: oportunidadeId
            });

            if (anexosRes.success && anexosRes.records) {
                const docsFormatados = anexosRes.records.map((anexo: any) => ({
                    id: anexo.id,
                    titulo: anexo.titulo,
                    data_upload: anexo.data_criacao || new Date().toISOString(),
                    tipo: anexo.tipo_arquivo || "Documento",
                    descricao: anexo.descricao || "",
                    nome_arquivo: anexo.nome_arquivo || `anexo-${anexo.id}.pdf`,
                    usuario_nome: anexo.usuario_nome || "Usuário",
                    tamanho: anexo.tamanho || 0
                }));
                setDocumentos(docsFormatados);
            } else if (anexosRes.success && anexosRes.data) {
                // Alternativa se os dados estiverem em "data" em vez de "records"
                const docsFormatados = anexosRes.data.map((anexo: any) => ({
                    id: anexo.id,
                    titulo: anexo.titulo,
                    data_upload: anexo.data_criacao || new Date().toISOString(),
                    tipo: anexo.tipo_arquivo || "Documento",
                    descricao: anexo.descricao || "",
                    nome_arquivo: anexo.nome_arquivo || `anexo-${anexo.id}.pdf`,
                    usuario_nome: anexo.usuario_nome || "Usuário",
                    tamanho: anexo.tamanho || 0
                }));
                setDocumentos(docsFormatados);
            }
        } catch (error) {
            console.error("Erro ao carregar documentos da oportunidade:", error);
            toast.error("Erro ao carregar documentos da oportunidade");
        } finally {
            setLoading(false);
        }
    };

    // Carregar dados ao montar o componente
    useEffect(() => {
        if (oportunidadeId) {
            loadDocumentos();
        }
    }, [oportunidadeId]);

    // Download de documento
    const handleDownload = async (anexoId: number, nomeArquivo: string) => {
        try {
            await apiService.downloadAnexo(anexoId, nomeArquivo);
            toast.success("Download iniciado com sucesso");
        } catch (error) {
            console.error("Erro ao fazer download do documento:", error);
            toast.error("Erro ao fazer download do documento");
        }
    };

    // Callback para recarregar documentos após adicionar novo anexo
    const handleAttachmentCreated = () => {
        loadDocumentos();
    };

    return (
        <>
            <div className="card">
                <div className="card-header d-flex align-items-center justify-content-between flex-wrap row-gap-3">
                    <h4 className="fw-semibold">Documentos da Oportunidade</h4>
                    <button 
                        className="btn btn-primary"
                        onClick={() => setShowAttachmentModal(true)}
                    >
                        <i className="ti ti-plus me-1"></i>
                        Novo Arquivo
                    </button>
                </div>
            <div className="card-body">
                {loading ? (
                    <div className="text-center p-3">
                        <div className="spinner-border" role="status">
                            <span className="visually-hidden">Carregando...</span>
                        </div>
                    </div>
                ) : documentos.length === 0 ? (
                    <div className="alert alert-info">
                        Nenhum documento da oportunidade registrado.
                    </div>
                ) : (
                    <div className="files-activity">
                        {documentos.map((doc) => (
                            <div className="card border shadow-none mb-3" key={doc.id}>
                                <div className="card-body pb-0">
                                    <div className="row align-items-center">
                                        <div className="col-md-8">
                                            <div className="mb-3">
                                                <h4 className="fw-medium mb-1">
                                                    {doc.titulo}
                                                </h4>
                                                <p>{doc.descricao || 'Sem descrição'}</p>
                                                <div className="d-flex align-items-center">
                                                    <span className="avatar avatar-sm avatar-gray avatar-rounded me-2 flex-shrink-0">
                                                        <img
                                                            src={`https://ui-avatars.com/api/?name=${encodeURIComponent(doc.usuario_nome || '')}&background=0D8ABC&color=fff`}
                                                            alt={doc.usuario_nome}
                                                            className="rounded-circle"
                                                        />
                                                    </span>
                                                    <div>
                                                        <span className="fs-12">Proprietário</span>
                                                        <p>{doc.usuario_nome || 'Usuário'}</p>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                        <div className="col-md-4 text-md-end">
                                            <div className="mb-3">
                                                <button
                                                    className="btn btn-sm btn-info"
                                                    onClick={() => handleDownload(doc.id, doc.nome_arquivo)}
                                                >
                                                    <i className="ti ti-download me-1"></i> Download
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
        
        {/* Modal de Anexação */}
        <AttachmentModal
            showAttachmentModal={showAttachmentModal}
            setShowAttachmentModal={setShowAttachmentModal}
            opportunityId={oportunidadeId}
            onAttachmentCreated={handleAttachmentCreated}
        />
    </>
    );
};

export default DocumentosOportunidade;